import { Form, FormSearchProps, Icon } from '@melio/penny';
import { useAnalytics } from '@melio/platform-analytics';
import { BusinessResultItem } from '@melio/platform-api';
import { useConfig, useMelioIntl } from '@melio/platform-provider';
import { forwardRef } from '@melio/platform-utils';

import { useBusinessSearchOptions } from './useBusinessSearchOptions';

export type BusinessSearchWidgetProps = Omit<FormSearchProps, 'creatableOption' | 'options' | 'emptyState'> & {
  onCreateOption?: (value: string) => void;
  onSelectOption: (value: BusinessResultItem) => void;
  onClearField: VoidFunction;
  isBusiness?: boolean;
};

export const BusinessSearchWidget = forwardRef<BusinessSearchWidgetProps, 'input'>(
  ({ onCreateOption, onSelectOption, onClearField, isBusiness, ...props }, ref) => {
    const { settings } = useConfig();

    const { getSelectedItemAnalyticsData, isLoadingBusinesses, optionsSections, handleSearch, getBusinessById } =
      useBusinessSearchOptions();

    const { formatMessage } = useMelioIntl();

    const { track } = useAnalytics();

    const handleCreateOption = (inputValue: string) => {
      track('Vendor', 'Click', getSelectedItemAnalyticsData());
      onCreateOption?.(inputValue);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const {
        target: { value },
      } = event;

      if (value) {
        const selectedBusiness = getBusinessById(value);
        if (selectedBusiness) {
          track('Vendor', 'Click', getSelectedItemAnalyticsData(selectedBusiness));
          onSelectOption(selectedBusiness);
          return;
        }
      }
      onClearField();
    };

    const badgeElement =
      isBusiness && settings.showCheckMarkIconForManagedVendor ? (
        <Icon size="small" type="verified" color="brand" />
      ) : undefined;

    return (
      // The ariaLabel and labelProps props are mutually exclusive, meaning that either labelProps or ariaLabel exists, never both simultaneously.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      <Form.Search
        data-component="BusinessSearchWidget"
        ref={ref}
        options={optionsSections}
        onInputChange={handleSearch}
        isLoading={isLoadingBusinesses}
        formatSelectedValue={({ label }) => label}
        creatableOption={{
          label: (value) => formatMessage('widgets.businessSearch.createLabel', { value }),
          onClick: handleCreateOption,
          shouldDisplay: () => true,
        }}
        emptyState={{
          label: formatMessage('widgets.businessSearch.emptyState'),
        }}
        valueRightElement={badgeElement}
        onChange={handleChange}
        labelProps={{ label: formatMessage('widgets.businessSearch.label') }}
        placeholder={formatMessage('widgets.businessSearch.placeholder')}
        {...props}
      />
    );
  }
);

BusinessSearchWidget.displayName = 'BusinessSearchWidget';
